import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '../../service/toast.service';
import { AssetsDataService } from '../../service/assets-data.service';
import { ReceivingLogApiService } from './api/receiving-log-api.service';
import { GetReceivingLogsResponse } from '../../models/logs/GetReceivingLogResponse';
import { ReceivingLogRow } from '../../models/logs/ReceivingLogRow';
import { ApiReceivingLogRow } from '../../models/logs/ApiReceivingLogRow';
import { ReceivingLogSearch } from '../../models/logs/ReceivingLogSearch';
import { LoadingText, ToastText } from '../../models/assets-data/UiConfig.interface';
import { legacyEndpointRefusalMessage } from './rules/log-edit-outcome.rules';

/**
 * ReceivingLogStateService manages the receiving-log table operations (fetch, update,
 * delete, restore-from-archive) and the shared cross-component state for the
 * receiving views.
 *
 * HTTP requests are delegated to ReceivingLogApiService. PO lookup and new-log
 * submission live in the stateless domain services (PoLookupService and
 * LogSubmissionService under src/app/domain/receiving-log) — components consume
 * those directly as observables.
 *
 * The BehaviorSubjects are:
 * - `fetchedLogs`: Holds the receiving logs fetched from the backend, updated when logs are retrieved.
 * - `dataLoading`: Tracks the loading state, allowing the UI to show loading indicators while data is being fetched or submitted.
 * - `logDataSubject` / `dateFriendlyLogDataSubject`: Hold the selected/in-progress log data for the preview components.
 */
@Injectable({
  providedIn: 'root',
})
export class ReceivingLogStateService {
  private _toastService: ToastService;
  private _assetsDataService: AssetsDataService;
  private _api: ReceivingLogApiService;

  public loadingText: LoadingText;
  public toastText: ToastText;

  public dataLoading = new BehaviorSubject<{ isLoading: boolean; message: string }>({
    isLoading: false,
    message: '',
  });
  public dataLoading$ = this.dataLoading.asObservable();
  private dataLoadingDefault = { isLoading: false, message: '' };

  public fetchedLogs = new BehaviorSubject<GetReceivingLogsResponse>(
    new GetReceivingLogsResponse()
  );
  public logDataSubject = new BehaviorSubject<ApiReceivingLogRow | null>(null);
  public logData$ = this.logDataSubject.asObservable();

  public dateFriendlyLogDataSubject = new BehaviorSubject<ReceivingLogRow | null>(null);
  public dateFriendlyLogData$ = this.dateFriendlyLogDataSubject.asObservable();

  constructor(
    toastService: ToastService,
    assetsDataService: AssetsDataService,
    api: ReceivingLogApiService
  ) {
    this._toastService = toastService;
    this._assetsDataService = assetsDataService;
    this._api = api;

    const uiConfigData = this._assetsDataService.GetUiConfigData();
    this.loadingText = uiConfigData.loadingText;
    this.toastText = uiConfigData.toastText;
  }

  public setLogRowData(logRowData: ApiReceivingLogRow): void {
    this.logDataSubject.next(logRowData);
  }

  public getLogRowDataSubject(): BehaviorSubject<ApiReceivingLogRow | null> {
    return this.logDataSubject;
  }

  public setDateFriendlyLogRowData(logRowData: ReceivingLogRow): void {
    this.dateFriendlyLogDataSubject.next(logRowData);
  }

  public getDateFriendlyLogRowDataSubject(): BehaviorSubject<ReceivingLogRow | null> {
    return this.dateFriendlyLogDataSubject;
  }

  /**
   * Fetches the receiving logs based on the search criteria. The `fetchedLogs`
   * BehaviorSubject is updated with the results, allowing any components
   * subscribed to it to automatically update with the newly fetched logs.
   *
   * Method is called within the `ReceivingLogComponent` when the component loads or when the user submits a search.
   *
   * @param receivingLogSearch The search criteria used to fetch the logs.
   */
  public fetchLogs(receivingLogSearch: ReceivingLogSearch): void {
    this.dataLoading.next({ isLoading: true, message: this.loadingText.loadingLogs });
    this._api.getReceivingLogs(receivingLogSearch).subscribe({
      next: (results) => {
        this.fetchedLogs.next(results);
      },

      error: (err: HttpErrorResponse) => {
        console.error(err);
        this._toastService.showWarningMessage(this.toastText.logEntry.fetch.error);
      },
      complete: () => {
        this.dataLoading.next(this.dataLoadingDefault);
      },
    });
  }

  /**
   * Updates an existing receiving log. On success, the optional callback is invoked
   * (the table view uses it to re-fetch logs).
   *
   * This method is called within the 'ReceivingLogComponent' when the user updates an existing log.
   *
   * @param receivingLogRow The receiving log data to update.
   * @param onSuccessFunction Optional callback function to execute after successful update.
   */
  public updateLog(
    receivingLogRow: ReceivingLogRow,
    onSuccessFunction?: Function
  ): void {
    this.dataLoading.next({ isLoading: true, message: this.loadingText.updatingLog });
    this._api.putReceivingLog(receivingLogRow).subscribe({
      next: () => {
        this._toastService.showSuccessMessage(this.toastText.logEntry.update.success);
        if (onSuccessFunction) {
          onSuccessFunction();
        }
      },

      error: (err: any) => {
        console.error(err);
        // `PUT /V1/update-receiving-log` refuses with a RecMgrOperationStatus body
        // (`{ isSuccess, message, userNote, statusCode }`), not a bare string — and
        // its mixed-container quantity refusal puts the actionable half in
        // `userNote`, which points at `PUT /V1/receiving-log/{logId}`
        // (handoff-18 §7). Discarding the body for the generic toast told the user
        // only that the save failed, never that there is another way to make it.
        const refusal = legacyEndpointRefusalMessage(err?.originalError?.error ?? err?.details?.body);
        this._toastService.showWarningMessage(refusal ?? this.toastText.logEntry.update.error, refusal ? 6000 : 3000);
      },
      complete: () => {
        this.dataLoading.next(this.dataLoadingDefault);
      },
    });
  }

  /**
   * Moves a receiving log from archive back to active table.
   *
   * This method can be called within components when the user restores an archived log entry.
   *
   * @param archiveRowId The ID of the archived receiving log to restore.
   * @param onSuccessFunction Optional callback function to execute after successful restoration.
   */
  public moveLogFromArchive(archiveRowId: number, onSuccessFunction?: Function): void {
    this.dataLoading.next({ isLoading: true, message: this.loadingText.restoringArchivedLog });

    this._api.moveReceivingLogFromArchive(archiveRowId).subscribe({
      next: (response) => {
        console.log(`Restored log from archive. New ID: ${response.NewId}`);
        this._toastService.showSuccessMessage(this.toastText.logEntry.archiveRestore.success);
        if (onSuccessFunction) {
          onSuccessFunction();
        }
      },

      error: (err: HttpErrorResponse) => {
        console.error(err);
        this._toastService.showWarningMessage(`${this.toastText.logEntry.archiveRestore.error}: ${err.message}`);
      },

      complete: () => {
        this.dataLoading.next(this.dataLoadingDefault);
      },
    });
  }

  /**
   * Deletes a receiving log by ID.
   *
   * This method is called within the 'ReceivingLogComponent' when the user deletes a log entry.
   *
   * @param logId The ID of the receiving log to delete.
   * @param onSuccessFunction Optional callback function to execute after successful deletion.
   */
  public deleteLog(logId: number, onSuccessFunction?: Function): void {
    this.dataLoading.next({ isLoading: true, message: this.loadingText.deletingLog });

    this._api.deleteReceivingLog(logId).subscribe({
      next: () => {
        this._toastService.showSuccessMessage(this.toastText.logEntry.delete.success);
        if (onSuccessFunction) {
          onSuccessFunction();
        }
      },

      error: (err: HttpErrorResponse) => {
        console.error(err);
        this._toastService.showWarningMessage(this.toastText.logEntry.delete.error);
      },

      complete: () => {
        this.dataLoading.next(this.dataLoadingDefault);
      },
    });
  }
}
